// stroomnet.js: het stroomnet van Fluvius op de kaarten van LaadAtlas, en de
// afstanden tot het net per locatie. Gedeeld door tabblad Laadpleinen en
// tabblad Plaatsing, zodat beide kaarten exact hetzelfde tonen.
//
// Alles hier doet niets zolang er geen GIS-bestand is: geen lagen, geen
// uitklapmenu, geen filter. Zie gisnet.js in de backend voor het inlezen.

import React from 'react';
import L from 'leaflet';

// ── Kleuren en volgorde, besluit HL 22 oktober 2026 (mockup V14) ──────────
// Zachtere netkleuren, besluit HL 26 oktober 2026 (mockup Kleuren kaart V1,
// variant A, "een fractie duidelijker"): het net is de ondergrond, de bollen
// van de kandidaten liggen erboven. Zelfde kleurfamilies als voorheen.
export const NET_STIJL = {
  v400: { color: '#7DBD5B', weight: 2.5, opacity: 0.72 },
  vonb: { color: '#F08A38', weight: 2, dashArray: '5 5', opacity: 0.75 },
  v230: { color: '#F08A38', weight: 3, opacity: 0.75 },
  ms:   { color: '#7E4BE0', weight: 2, dashArray: '6 6', opacity: 0.75 },
};
const PAARS = '#7E55D6';
const KAST = { fill: '#FFC400', rand: '#9A6A00' };

// Van onder naar boven. De kandidaatlocaties en geplotte palen liggen altijd
// bovenop, in hun eigen laag 'p_kand'. De volgorde blijft gelijk, ook als een
// laag uit en weer aan wordt gezet.
// Sinds 26 oktober 2026 ook de lagen uit het planbestand van Fluvius: werken
// en zoekzones onderaan, de band onder een kabel die versterkt moet worden
// daarboven, de ring om een cabine met een verwacht probleem boven de cabine.
// De rode lijn "net te versterken" ligt sinds 26 oktober 2026 als dunne lijn
// over de kabel heen in plaats van als brede band eronder.
const VOLGORDE = ['prj', 'zz', 'v400', 'vonb', 'v230', 'ms', 'vst', 'vk', 'kc', 'dc', 'tr', 'kand'];

// Kleuren van het plan, los van die van het net (mockup Plan van Fluvius V1).
export const PLAN_KLEUR = { vst: '#D7263D', tr: '#D7263D', zz: '#6A00E0', prj: '#4B5BA6' };

// Maakt de lagen eenmalig aan op een kaart. Veilig om vaker aan te roepen.
// basis: de hoogte van de onderste laag. Standaard 410, net boven de gewone
// lijnen van Leaflet. Op het technisch fiche 300: daar moet de schets van
// rijen, vakken en palen bovenop het stroomnet blijven liggen. Stappen van 5,
// zodat ook met de lagen van het plan alles onder 400 blijft op het fiche.
export function maakNetPanes(map, basis = 410) {
  if (!map || map._netPanes) return;
  map._netRenderer = {};
  VOLGORDE.forEach((k, i) => {
    const p = map.createPane(`p_${k}`);
    p.style.zIndex = String(basis + i * 5);
    map._netRenderer[k] = L.canvas({ pane: `p_${k}` });
  });
  map._netPanes = true;
}

// De laag waarin kandidaten en palen getekend worden, bovenop het net.
export const KAND_PANE = 'p_kand';

// Een cabine als vierkantje. Uitgezoomd kleiner, anders dekken de cabines van
// een stad als Leuven (533) de hele kaart af.
const vierkantMaat = (zoom) => (zoom >= 16 ? 10 : zoom >= 14 ? 7 : 4);
const vierkant = (vul, rand, maat = 10) => {
  const r = maat >= 7 ? 2 : 1, tot = maat + 2 * r + 2 * r;
  return L.divIcon({
    className: '', iconSize: [tot, tot], iconAnchor: [tot / 2, tot / 2],
    html: `<div style="width:${maat}px;height:${maat}px;background:${vul};border:${r}px solid #fff;box-shadow:0 0 0 ${r}px ${rand}"></div>`,
  });
};

// Tekent het net op een kaart, of haalt het weg als lagen leeg is. zicht zegt
// per laag of hij aan staat.
// interactief: false op het technisch fiche, zodat een klik om een rij of paal
// te tekenen niet op een kast of cabine blijft hangen.
// De dikte van de lijnen volgt het zoomniveau (melding HL 26 oktober 2026: bij
// een hele stad werden de lijnen te dik). Vanaf zoom 17 de volle dikte uit
// NET_STIJL, daaronder steeds dunner, nooit dunner dan 1 pixel.
const ZOOM_FACTOR = { 11: 0.3, 12: 0.35, 13: 0.45, 14: 0.55, 15: 0.7, 16: 0.85 };
export function lijnDikte(basis, zoom) {
  const f = zoom >= 17 ? 1 : (ZOOM_FACTOR[Math.floor(zoom)] ?? 0.3);
  return Math.max(1, Math.round(basis * f * 10) / 10);
}
const LIJN_BASIS = { v400: NET_STIJL.v400.weight, vonb: NET_STIJL.vonb.weight, v230: NET_STIJL.v230.weight,
  ms: NET_STIJL.ms.weight, vst: 2 };

function zetDiktes(map) {
  const g = map._stroomnet;
  if (!g) return;
  const z = map.getZoom();
  for (const k of Object.keys(LIJN_BASIS)) {
    if (g[k] && g[k].setStyle) g[k].setStyle({ weight: lijnDikte(LIJN_BASIS[k], z) });
  }
  // Cabines, kasten en ringen alleen opnieuw als de maat echt verandert.
  const maat = vierkantMaat(z);
  if (map._netMaat !== maat) {
    map._netMaat = maat;
    g.dc?.eachLayer(m => m.setIcon(vierkant(PAARS, PAARS, maat)));
    g.kc?.eachLayer(m => m.setIcon(vierkant('#fff', PAARS, maat)));
    g.vk?.eachLayer(m => m.setRadius(maat >= 10 ? 5 : maat >= 7 ? 4 : 2.5));
    g.tr?.eachLayer(m => m.setRadius(maat >= 10 ? 12 : maat >= 7 ? 9 : 6));
  }
}

export function tekenStroomnet(map, lagen, zicht, interactief = true) {
  if (!map) return;
  maakNetPanes(map);
  if (map._stroomnetBron !== lagen) {
    if (map._stroomnet) Object.values(map._stroomnet).forEach(g => map.removeLayer(g));
    map._stroomnet = null;
    map._stroomnetBron = lagen;
    if (lagen) {
      const R = map._netRenderer;
      const g = {};
      // Eén lijn met vele delen per laag, in plaats van een losse lijn per
      // kabelstuk. Bij Leuven zijn dat 63.670 stukken; als losse lijnen liep
      // het tekenen bij zoomen en schuiven achter op de kaart.
      const z = map.getZoom();
      for (const k of ['v400', 'vonb', 'v230', 'ms']) {
        g[k] = L.polyline(lagen[k] || [], { ...NET_STIJL[k], weight: lijnDikte(NET_STIJL[k].weight, z),
          pane: `p_${k}`, renderer: R[k], interactive: false, smoothFactor: 1.5 });
      }
      const tip = (laag, tekst) => (interactief ? laag.bindTooltip(tekst) : laag);
      const maat = vierkantMaat(z);
      map._netMaat = maat;
      g.vk = L.layerGroup((lagen.vk || []).map(p => tip(L.circleMarker(p, {
        pane: 'p_vk', renderer: R.vk, radius: maat >= 10 ? 5 : maat >= 7 ? 4 : 2.5, color: KAST.rand, weight: maat >= 7 ? 2 : 1, interactive: interactief,
        fillColor: KAST.fill, fillOpacity: 1 }), 'Verdeelkast')));
      g.dc = L.layerGroup((lagen.dc || []).map(p => tip(L.marker(p, {
        pane: 'p_dc', icon: vierkant(PAARS, PAARS, maat), interactive: interactief }), 'MS-cabine Fluvius')));
      g.kc = L.layerGroup((lagen.kc || []).map(p => tip(L.marker(p, {
        pane: 'p_kc', icon: vierkant('#fff', PAARS, maat), interactive: interactief }), 'MS-cabine van een bedrijf')));
      // De lagen van het plan, alleen als er een planbestand is ingelezen.
      g.vst = L.polyline(lagen.versterken || [], {
        pane: 'p_vst', renderer: R.vst, color: PLAN_KLEUR.vst, weight: lijnDikte(2, z), opacity: 0.9,
        interactive: false, smoothFactor: 1.5 });
      g.tr = L.layerGroup((lagen.transfoProbleem || []).map(p => L.circleMarker(p, {
        pane: 'p_tr', renderer: R.tr, radius: maat >= 10 ? 12 : maat >= 7 ? 9 : 6, color: PLAN_KLEUR.tr, weight: maat >= 7 ? 3 : 2,
        fill: false, interactive: false })));
      g.zz = L.layerGroup((lagen.zoekzones || []).map(r => L.polygon(r, {
        pane: 'p_zz', renderer: R.zz, color: PLAN_KLEUR.zz, weight: 2, dashArray: '6 5',
        fillColor: PLAN_KLEUR.zz, fillOpacity: 0.12, interactive: false })));
      // De werken van Fluvius in de drie groepen die Fluvius zelf gebruikt,
      // elk met een eigen lijnsoort (besluit HL 26 oktober 2026).
      for (const [sl, groep] of [['prjR', 'regulier'], ['prjP', 'proactief'], ['prjC', 'cabine']]) {
        g[sl] = L.layerGroup((lagen.projecten || []).filter(p => werkGroep(p.categorie) === groep).map(p => tip(L.polygon(p.ring, {
          pane: 'p_prj', renderer: R.prj, color: PLAN_KLEUR.prj, weight: 2, dashArray: WERK_STREEP[groep],
          fillColor: PLAN_KLEUR.prj, fillOpacity: 0.05, interactive: interactief }), `${WERK_NAAM[groep]}: ${werkLeesbaar(p.categorie)}`)));
      }
      map._stroomnet = g;
      if (!map._netDikte) { map._netDikte = true; map.on('zoomend', () => zetDiktes(map)); }
    }
  }
  if (!map._stroomnet) return;
  for (const [k, g] of Object.entries(map._stroomnet)) {
    const aan = zicht?.[k] !== false;
    if (aan && !map.hasLayer(g)) g.addTo(map);
    if (!aan && map.hasLayer(g)) map.removeLayer(g);
  }
}

// Standaard staan alle lagen aan, behalve de cabines van bedrijven.
// De lagen van het plan staan standaard uit, zodat de kaart rustig blijft.
export const STANDAARD_ZICHT = { v400: true, v230: true, vonb: true, ms: true, dc: true, vk: true, kc: false,
  vst: false, tr: false, zz: false, prjR: false, prjP: false, prjC: false };

// De drie groepen werken, zoals Fluvius ze in zijn eigen kaartpakket indeelt:
// proactief is alles met "transitie", cabineprojecten zijn nieuwe of te
// moderniseren distributiecabines en de soorten SP en TS, de rest is regulier.
export function werkGroep(cat) {
  const c = String(cat || '');
  if (/transitie/i.test(c)) return 'proactief';
  if (/cabine/i.test(c) || c === 'SP' || c === 'TS') return 'cabine';
  return 'regulier';
}
export const WERK_NAAM = { regulier: 'Reguliere projecten van Fluvius', proactief: 'Proactieve projecten van Fluvius',
  cabine: 'Cabineprojecten van Fluvius' };
const WERK_STREEP = { regulier: '2 5', proactief: '8 5', cabine: null };

// De soorten werken van Fluvius in gewone taal (mockup Wijk impact, akkoord HL
// 27 oktober 2026). HV, LV en ZHV staan niet uitgelegd in het bestand; de
// vertaling volgt de lezing van HL en is bij Fluvius te bevestigen. Een soort
// die hier niet staat, verschijnt zoals Fluvius hem noemt.
export const WERK_LEESBAAR = {
  'HV uitbreiding net': 'Uitbreiding net voor hoog vermogen (HV)',
  'ZHV uitbreiding net': 'Uitbreiding net voor zeer hoog vermogen (ZHV)',
  'LV uitbreiding net': 'Uitbreiding net voor laag vermogen (LV)',
  'Nettransitie - Digitale connector': 'Nettransitie, digitale connector',
  'Regio - Energietransitie': 'Energietransitie in de regio',
  'Initiatief Regio': 'Initiatief van de regio',
  'Werken voor derden': 'Werken voor derden',
  'Wegeniswerken': 'Wegenwerken',
  'Moderniseren Distributiecabine': 'Modernisering van een MS-cabine',
  'Nieuwe distributiecabine': 'Nieuwe MS-cabine',
  'Verkaveling': 'Verkaveling',
  'Ruggengraat / Investeringen': 'Versterking van het hoofdnet',
  'Ondergronds brengen': 'Kabels ondergronds brengen',
  'Appartementsgebouw': 'Aansluiting appartementsgebouw',
  'Verplaatsingswerk': 'Verplaatsing van leidingen',
  'Verplaatsingswerk met facturatie vooraf': 'Verplaatsing van leidingen',
  'Extra werken': 'Extra werken',
  'SP': 'Cabineproject (SP)',
  'TS': 'Cabineproject (TS)',
};
export function werkLeesbaar(cat) {
  return WERK_LEESBAAR[cat] || String(cat || '');
}

// ── Het uitklapmenu Stroomnet, onderaan een bestaande legenda ─────────────
function km(v) {
  const n = Number(v) || 0;
  return n >= 10 ? Math.round(n).toLocaleString('nl-BE') : (Math.round(n * 10) / 10).toFixed(1).replace('.', ',');
}
function peildatumKort(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' });
}

export const NET_OPMERKING = 'De netgegevens tonen waar kabels, cabines en kasten liggen. '
  + 'Ze zeggen niets over de vrije capaciteit op het net en zijn geen toezegging van de netbeheerder.';

// vast: zonder uitklappen, als gewone groep in een paneel (tabblad Laadpleinen,
// paneel Lagen, mockup V7).
// los: zonder lijn erboven, als eigen vak op een kaart (technisch fiche).
// bestaand: het aantal bestaande laadpunten; dan komt er een regel bij om ze
// aan en uit te zetten, met de sleutel 'best' in zicht (technisch fiche V2).
export function StroomnetUitklap({ gis, zicht, onZicht, vast = false, los = false, bestaand = null }) {
  if (!gis?.aanwezig) return null;
  const sv = gis.samenvatting || {};
  const lijn = (stijl) => (
    <span style={{ width: 24, height: 0, display: 'inline-block', flexShrink: 0,
      borderTop: `${Math.max(2, Math.round(stijl.weight))}px ${stijl.dashArray ? 'dashed' : 'solid'} ${stijl.color}` }} />
  );
  const vk = (vul, rand) => (
    <span style={{ width: 11, height: 11, display: 'inline-block', flexShrink: 0, margin: '0 6px',
      background: vul, boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${rand}` }} />
  );
  const bol = (
    <span style={{ width: 11, height: 11, borderRadius: '50%', display: 'inline-block', flexShrink: 0, margin: '0 6px',
      background: KAST.fill, boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${KAST.rand}` }} />
  );
  // Het planbestand van Fluvius heeft geen middenspanningskabels,
  // verdeelkasten of MS-cabines van bedrijven; die regels vallen dan weg.
  const plan = sv.soort === 'plan';
  const regels = [
    ['grp', 'Middenspanning'],
    ['ms', 'Middenspanningskabel', lijn(NET_STIJL.ms), `${km(sv.kmMidden)} km`],
    ['dc', 'MS-cabine Fluvius, zet om naar stroom voor de straat', vk(PAARS, PAARS), sv.cabinesFluvius],
    ['kc', 'MS-cabine van een bedrijf, met eigen aansluiting', vk('#fff', PAARS), sv.cabinesBedrijf],
    ['grp', 'Straat'],
    ['v400', 'Straatkabel 400 V', lijn(NET_STIJL.v400), `${km(sv.km400)} km`],
    ['v230', 'Straatkabel 230 V', lijn(NET_STIJL.v230), `${km(sv.km230)} km`],
    ['vonb', 'Straatkabel, spanning niet vermeld', lijn(NET_STIJL.vonb), `${km(sv.kmOnbekend)} km`],
    ['vk', 'Verdeelkast, aftakpunt van de straatkabel', bol, sv.verdeelkasten],
    ...(sv.plan ? [['grp', 'Plan van Fluvius'],
      ['vst', 'Net te versterken', <span style={{ width: 24, height: 0, display: 'inline-block', flexShrink: 0,
        borderTop: `2px solid ${PLAN_KLEUR.vst}` }} />, `${km(sv.plan.kmVersterken)} km`],
      ['tr', 'MS-cabine, transformator verwacht probleem', <span style={{ width: 13, height: 13, borderRadius: '50%',
        display: 'inline-block', flexShrink: 0, margin: '0 5px', border: `2.5px solid ${PLAN_KLEUR.tr}`, boxSizing: 'border-box' }} />,
        sv.plan.transfoProbleem],
      ['zz', 'Zoekzone nieuwe cabine', <span style={{ width: 16, height: 12, display: 'inline-block', flexShrink: 0, margin: '0 4px',
        background: `${PLAN_KLEUR.zz}26`, border: `1.5px dashed ${PLAN_KLEUR.zz}`, boxSizing: 'border-box' }} />, sv.plan.zoekzones],
      ...[['prjR', 'regulier', 'dotted'], ['prjP', 'proactief', 'dashed'], ['prjC', 'cabine', 'solid']].map(([sl, groep, rand]) => [
        sl, WERK_NAAM[groep], <span style={{ width: 16, height: 12, display: 'inline-block', flexShrink: 0, margin: '0 4px',
          border: `1.5px ${rand} ${PLAN_KLEUR.prj}`, boxSizing: 'border-box' }} />,
        sv.plan.projectenGroep ? sv.plan.projectenGroep[groep] : ''])] : []),
    ...(bestaand != null ? [['grp', 'Laadpunten'],
      ['best', 'Bestaande laadpunten', <BestaandTeken key="b" />, bestaand]] : []),
  ];
  const inhoud = (
      <div style={{ marginTop: 6 }}>
        {regels.filter(([k]) => !(plan && (['ms', 'kc', 'vk'].includes(k) || (k === 'vonb' && !Number(sv.kmOnbekend))))).map(([k, label, teken, aantal], i) => k === 'grp' ? (
          <div key={i} style={{ fontSize: 12, fontWeight: 700, color: '#6B7684', textTransform: 'uppercase',
                                letterSpacing: '.05em', margin: '8px 0 2px' }}>{label}</div>
        ) : (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={zicht?.[k] !== false}
              onChange={e => onZicht({ ...zicht, [k]: e.target.checked })} />
            {teken}
            <span style={{ flex: 1 }}>{label}</span>
            <b style={{ whiteSpace: 'nowrap' }}>{typeof aantal === 'number' ? aantal.toLocaleString('nl-BE') : aantal}</b>
          </label>
        ))}
        <div style={{ fontSize: 12.5, color: '#6B7684', marginTop: 6, lineHeight: 1.4 }}>{NET_OPMERKING}</div>
        <div style={{ fontSize: 12.5, color: '#6B7684', marginTop: 4 }}>Bron: Fluvius, peildatum {peildatumKort(gis.peildatum)}</div>
        {sv.plan && <div style={{ fontSize: 12.5, color: '#6B7684', marginTop: 6, lineHeight: 1.4 }}>{planUitleg(sv.plan)}</div>}
      </div>
  );
  if (vast) return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7684', textTransform: 'uppercase', letterSpacing: '.05em' }}>Stroomnet</div>
      {inhoud}
    </div>
  );
  return (
    <details style={los ? {} : { borderTop: '1px solid #D9D5CD', marginTop: 8, paddingTop: 8 }}>
      <summary style={{ fontWeight: 700, cursor: 'pointer', fontSize: 13.5 }}>Stroomnet</summary>
      {inhoud}
    </details>
  );
}

// ── Bestaande laadpunten ────────────────────────────────────────────────
// Donkergrijze bol met witte rand. Zo botsen ze niet met de kleuren van de
// nieuwe palen (blauw, oker, rood). Besluit HL 26 oktober 2026, fiche V2.
export const BESTAAND_KLEUR = '#3D4652';
export function BestaandTeken() {
  return (
    <span style={{ width: 10, height: 10, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
      margin: '0 6px', background: BESTAAND_KLEUR, boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${BESTAAND_KLEUR}` }} />
  );
}

// ── Aansluiten op het net (technisch fiche, deel B) ──────────────────────
// De soorten aansluitpunt, in de volgorde van het fiche. ms alleen vanaf
// 25 kVA: onder die grens komt de aansluiting altijd op laagspanning.
export const AANSLUIT_SOORTEN = [
  ['dc',   'MS-cabine Fluvius, laagspanning uit de cabine'],
  ['vk',   'Verdeelkast'],
  ['v400', 'Straatkabel 400 V'],
  ['ms',   'Middenspanningskabel'],
  ['kc',   'MS-cabine van een bedrijf'],
  ['vrij', 'Zelf aangewezen punt'],
];
export const AANSLUIT_KLEUR = '#E0218A';

// Het teken bij elke soort, gelijk aan de kaart en het uitklapmenu.
export function AansluitTeken({ soort }) {
  if (soort === 'dc' || soort === 'kc') {
    return <span style={{ width: 10, height: 10, display: 'inline-block', flexShrink: 0, margin: '0 4px',
      background: soort === 'dc' ? PAARS : '#fff', boxShadow: `0 0 0 1px ${PAARS}` }} />;
  }
  if (soort === 'vk') {
    return <span style={{ width: 10, height: 10, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
      margin: '0 4px', background: KAST.fill, boxShadow: `0 0 0 1px ${KAST.rand}` }} />;
  }
  if (soort === 'v400' || soort === 'ms') {
    const stijl = NET_STIJL[soort];
    return <span style={{ width: 18, height: 0, display: 'inline-block', flexShrink: 0,
      borderTop: `${soort === 'ms' ? 2 : 3}px ${stijl.dashArray ? 'dashed' : 'solid'} ${stijl.color}` }} />;
  }
  return <span style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
    margin: '0 4px', background: '#fff', border: `2px solid ${AANSLUIT_KLEUR}` }} />;
}

// Afstand hemelsbreed in meter tussen twee [lat, lng].
export function meterTussen(a, b) {
  const r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * r, dLng = (b[1] - a[1]) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371008 * Math.asin(Math.sqrt(h));
}

// Het dichtstbijzijnde punt van elke soort vanaf start [lat, lng]: de
// dichtstbijzijnde cabine of kast, en het dichtstbijzijnde punt OP een kabel
// (niet het dichtstbijzijnde knikpunt). Rekent in een vlak rond het
// beginpunt; op enkele kilometers scheelt dat minder dan een meter.
// Geeft { dc, vk, kc, v400, ms }, elk { lat, lng, d } of null.
export function dichtsteNetpunten(start, lagen) {
  const uit = { dc: null, vk: null, kc: null, v400: null, ms: null };
  if (!start || !lagen) return uit;
  const kx = 111320 * Math.cos(start[0] * Math.PI / 180), ky = 110574;
  const xy = (p) => [(p[1] - start[1]) * kx, (p[0] - start[0]) * ky];
  const terug = ([x, y]) => [start[0] + y / ky, start[1] + x / kx];
  for (const k of ['dc', 'vk', 'kc']) {
    let best = null, bestD = Infinity;
    for (const p of lagen[k] || []) {
      const [x, y] = xy(p);
      const d = x * x + y * y;
      if (d < bestD) { bestD = d; best = p; }
    }
    if (best) uit[k] = { lat: best[0], lng: best[1], d: Math.round(meterTussen(start, best)) };
  }
  for (const k of ['v400', 'ms']) {
    let best = null, bestD = Infinity;
    for (const lijn of lagen[k] || []) {
      for (let i = 1; i < lijn.length; i++) {
        const [ax, ay] = xy(lijn[i - 1]), [bx, by] = xy(lijn[i]);
        const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
        const t = l2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / l2)) : 0;
        const px = ax + t * dx, py = ay + t * dy;
        const d = px * px + py * py;
        if (d < bestD) { bestD = d; best = [px, py]; }
      }
    }
    if (best) {
      const p = terug(best);
      uit[k] = { lat: p[0], lng: p[1], d: Math.round(meterTussen(start, p)) };
    }
  }
  return uit;
}

// ── Plan van Fluvius: de uitkomst per locatie ──────────────────────────
// Teksten goedgekeurd met de mockup Plan van Fluvius V1, 26 oktober 2026.
export function planUitleg(plan) {
  // "Op lange termijn": de lagen heten bij Fluvius "Evolutie laagspanningsnet"
  // en "Evolutie bestaande cabines" en het kaartpakket beschrijft zichzelf als
  // lange-termijn evolutie van het net. Een jaartal staat er niet in
  // (besluit HL 27 oktober 2026: zo dicht mogelijk bij de tekst van Fluvius).
  return 'Plan van Fluvius: waar Fluvius op lange termijn verwacht het net te moeten versterken of een nieuwe cabine zoekt, '
    + 'en waar Fluvius werken heeft, zonder datum. Geen planning en geen toezegging. '
    + `Bron: Fluvius, planbestand, aangemaakt op ${peildatumKort(plan?.peildatum)}.`;
}
export function planVoet(plan) {
  return `Bron: Fluvius, planbestand, aangemaakt op ${peildatumKort(plan?.peildatum)}. Geen planning en geen toezegging.`;
}

function inRing(p, r) {
  let o = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [yi, xi] = r[i], [yj, xj] = r[j];
    if (((yi > p[0]) !== (yj > p[0])) && (p[1] < (xj - xi) * (p[0] - yi) / (yj - yi) + xi)) o = !o;
  }
  return o;
}
function afstandTotLijnen(p, lijnen) {
  const kx = 111320 * Math.cos(p[0] * Math.PI / 180), ky = 110574;
  let best = Infinity;
  for (const l of lijnen || []) {
    for (let i = 1; i < l.length; i++) {
      const ax = (l[i - 1][1] - p[1]) * kx, ay = (l[i - 1][0] - p[0]) * ky;
      const bx = (l[i][1] - p[1]) * kx, by = (l[i][0] - p[0]) * ky;
      const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
      const t = l2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / l2)) : 0;
      const d = Math.hypot(ax + t * dx, ay + t * dy);
      if (d < best) best = d;
    }
  }
  return best;
}

// Wat het plan zegt over een plek. Vanaf hetzelfde punt als de netgegevens.
// - De kabel: ligt de dichtstbijzijnde straatkabel van 400 V op een stuk dat
//   Fluvius wil versterken? Binnen 1 m telt als hetzelfde stuk: met een
//   detailbestand en een planbestand samen liggen de lijnen niet exact op
//   elkaar.
// - De cabine: heeft de dichtstbijzijnde MS-cabine een verwacht probleem met
//   de transformator? Binnen 2 m telt als dezelfde cabine.
// - Een zoekzone: erin, of binnen 500 m.
// - Werken: in welke werkgebieden de plek ligt.
// Geeft null zonder planbestand.
export function planSignalen(punt, lagen) {
  if (!punt || !lagen || !(lagen.versterken || lagen.transfoProbleem || lagen.zoekzones || lagen.projecten)) return null;
  const np = dichtsteNetpunten(punt, lagen);
  const uit = { regels: [], kabelVersterken: null, transfoProbleem: null, zoekzone: null, werken: [] };
  const f = (n) => Math.round(n).toLocaleString('nl-BE');
  // Verder dan 5 km zegt niets over deze plek; dezelfde grens als de server.
  if (np.v400 && np.v400.d <= 5000 && (lagen.versterken || []).length) {
    const dv = afstandTotLijnen(punt, lagen.versterken);
    uit.kabelVersterken = dv <= np.v400.d + 1;
    uit.regels.push(uit.kabelVersterken
      ? ['let', PLAN_KLEUR.vst, `Straatkabel 400 V op ${f(np.v400.d)} m: in het plan van Fluvius staat deze kabel als 'te versterken' op lange termijn.`]
      : ['ok', '#3F7D4E', `Straatkabel 400 V op ${f(np.v400.d)} m: in het plan van Fluvius niet als 'te versterken'.`]);
  }
  if (np.dc && np.dc.d <= 5000 && lagen.transfoProbleem) {
    uit.transfoProbleem = lagen.transfoProbleem.some(q => meterTussen(q, [np.dc.lat, np.dc.lng]) <= 2);
    uit.regels.push(uit.transfoProbleem
      ? ['let', PLAN_KLEUR.tr, `MS-cabine Fluvius op ${f(np.dc.d)} m: in het plan van Fluvius staat bij de transformator van deze cabine 'verwacht probleem' op lange termijn.`]
      : ['ok', '#3F7D4E', `MS-cabine Fluvius op ${f(np.dc.d)} m: in het plan van Fluvius geen verwacht probleem bij de transformator.`]);
  }
  const zz = lagen.zoekzones || [];
  if (zz.length) {
    if (zz.some(r => inRing(punt, r))) {
      uit.zoekzone = 0;
      uit.regels.push(['info', PLAN_KLEUR.zz, 'Ligt in een zoekzone voor een nieuwe cabine van Fluvius.']);
    } else {
      const d = Math.min(...zz.map(r => afstandTotLijnen(punt, [[...r, r[0]]])));
      uit.zoekzone = Math.round(d);
      if (d <= 500) uit.regels.push(['info', PLAN_KLEUR.zz, `Zoekzone voor een nieuwe cabine op ${f(d)} m.`]);
    }
  }
  uit.werken = [...new Set((lagen.projecten || []).filter(p => inRing(punt, p.ring)).map(p => werkLeesbaar(p.categorie)))];
  if (uit.werken.length) {
    uit.regels.push(['info', PLAN_KLEUR.prj,
      `Ligt in een gebied waar Fluvius werken heeft: ${uit.werken.join(', ').toLowerCase()}. Kans om de aansluiting mee te nemen; datum niet bekend.`]);
  }
  return uit;
}

// Het blok "Plan van Fluvius" onder de netgegevens.
export function PlanBlok({ signalen, plan, kop = true, kopStijl }) {
  if (!signalen || !signalen.regels.length) return null;
  const achter = { let: '#FDF0EE', ok: '#EEF6F0', info: '#F1F2F8' };
  const voor = { let: '#8E2A1D', ok: '#2C5741', info: '#2F3A73' };
  return (
    <div>
      {kop && <div style={kopStijl || { fontSize: 13.5, fontWeight: 700, color: '#12161C', margin: '14px 0 8px' }}>Plan van Fluvius</div>}
      {signalen.regels.map(([soort, kleur, tekst]) => (
        <div key={tekst} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13.5, lineHeight: 1.45,
          padding: '7px 10px', borderRadius: 7, marginBottom: 6, background: achter[soort], color: voor[soort] }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: kleur, flexShrink: 0, marginTop: 3 }} />
          <span>{tekst}</span>
        </div>
      ))}
      <div style={{ fontSize: 12.5, color: '#6B7684', lineHeight: 1.45, marginTop: 4 }}>{planVoet(plan)}</div>
    </div>
  );
}

// ── Nabij het net: de uitkomst per locatie ─────────────────────────────────
// afst: { d400, d230, dms } in meter. eigenCabine: een laadplein boven 250 kVA,
// dat op de middenspanning wordt beoordeeld in plaats van op de straatkabel.
export function netKlasse(afst, grens, eigenCabine = false) {
  if (!afst) return null;
  const binnen = (d) => d != null && d <= grens;
  if (eigenCabine) return binnen(afst.dms) ? 'ms' : 'geenms';
  if (binnen(afst.d400)) return '400';
  if (binnen(afst.d230)) return '230';
  return 'geen';
}

export const NET_KLASSE = {
  '400':    { kleur: '#3F7D4E', label: (g) => `400 V binnen ${g} m`, kort: '400 V' },
  '230':    { kleur: '#E06A00', label: (g) => `alleen 230 V binnen ${g} m`, kort: 'alleen 230 V' },
  geen:     { kleur: '#B4442F', label: (g) => `geen straatkabel binnen ${g} m`, kort: 'geen straatkabel' },
  ms:       { kleur: '#3F7D4E', label: (g) => `middenspanning binnen ${g} m`, kort: 'middenspanning' },
  geenms:   { kleur: '#B4442F', label: (g) => `geen middenspanning binnen ${g} m`, kort: 'geen middenspanning' },
};

// Een klasse telt als "binnen bereik van het net".
export const NET_OK = (k) => k === '400' || k === 'ms';

export function NetLabel({ klasse, grens }) {
  if (!klasse) return null;
  const k = NET_KLASSE[klasse];
  return (
    <span style={{ display: 'inline-block', marginTop: 4, fontSize: 12.5, fontWeight: 700, color: '#fff',
                   background: k.kleur, borderRadius: 4, padding: '1px 7px' }}>{k.label(grens)}</span>
  );
}

// De sleutel waarmee een geplotte paal zijn afstand terugvindt. Twee palen op
// dezelfde meter bestaan niet, dus de coördinaat volstaat.
export const paalSleutel = (p) => `${Number(p.lat).toFixed(6)}_${Number(p.lng).toFixed(6)}`;

// De regel met afstanden, zoals die onder een kandidaat en in de hovertekst
// staat. Een afstand die niet bekend is (verder dan 5 km) wordt weggelaten.
const deel = (tekst, d) => (d == null ? null : `${tekst} op ${Number(d).toLocaleString('nl-BE')} m`);
// Voor een laadplein geen oordeel, alleen de afstanden: een plein krijgt een
// eigen aansluiting en mag verder van het net liggen dan een losse paal.
// Besluit HL 23 oktober 2026. De middenspanningskabel telt alleen voor een
// plein boven 250 kVA, dat een eigen cabine nodig heeft (metMs).
export function netRegelPlein(a, metMs = false) {
  if (!a) return '';
  return [deel('MS-cabine Fluvius', a.dcabine), deel('verdeelkast', a.dkast), deel('straatkabel 400 V', a.d400),
          metMs ? deel('middenspanningskabel', a.dms) : null]
    .filter(Boolean).join(' · ');
}
export function netRegelPaal(a) {
  if (!a) return '';
  const regel = [deel('Straatkabel 400 V', a.d400), deel('verdeelkast', a.dkast)].filter(Boolean).join(' · ');
  return (a.d230 != null && (a.d400 == null || a.d230 < a.d400)) ? `${regel} · alleen 230 V op ${a.d230} m` : regel;
}
// Alleen 230 V dichterbij dan 400 V, voor het oranje label.
export const alleen230 = (a) => !!a && a.d230 != null && (a.d400 == null || a.d230 < a.d400);

export function peildatumLang(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Tabel op Plaatsing: één regel per straat, soms met meer palen ──────────
// Eén paal: de afstand zelf. Meer palen: van de kortste tot de langste.
export function netBereik(lijst, veld) {
  const w = (lijst || []).map(a => a?.[veld]).filter(d => d != null);
  if (!w.length) return '';
  const lo = Math.min(...w), hi = Math.max(...w);
  const f = (d) => Number(d).toLocaleString('nl-BE');
  return lo === hi ? f(lo) : `${f(lo)} tot ${f(hi)}`;
}

// De uitkomst per straat. Alle palen gelijk: het label. Anders per uitkomst
// het aantal palen, bijvoorbeeld "2 × 400 V, 1 × alleen 230 V".
export function NetStraat({ lijst, grens }) {
  if (!lijst?.length) return null;
  const tel = {};
  for (const a of lijst) { const k = netKlasse(a, grens, false); tel[k] = (tel[k] || 0) + 1; }
  const soorten = Object.keys(tel);
  if (soorten.length === 1) {
    const a = lijst[0];
    return (
      <span>
        <NetLabel klasse={soorten[0]} grens={grens} />
        {lijst.length === 1 && soorten[0] === 'geen' && alleen230(a) && (
          <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: NET_KLASSE['230'].kleur, marginTop: 2 }}>
            alleen 230 V op {Number(a.d230).toLocaleString('nl-BE')} m</span>
        )}
      </span>
    );
  }
  return (
    <span style={{ fontSize: 12.5 }}>
      {['400', '230', 'geen'].filter(k => tel[k]).map((k, i) => (
        <span key={k}>{i > 0 ? ', ' : ''}<b style={{ color: NET_KLASSE[k].kleur }}>{tel[k]} × {NET_KLASSE[k].kort}</b></span>
      ))}
    </span>
  );
}
